/**
 * MemberForum — Facebook-style discussion board embedded on every
 * member/memorial page. Threaded replies (one level deep), emoji
 * reactions, memory + location tags, edit/delete.
 *
 * Props:
 *   - memberId (required): family_members.id
 *   - canWrite (bool):     whether the current user may post
 *   - meUid    (string):   firebase_uid of the viewer (for edit/delete affordances)
 */
import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageCircle, Heart, Send, Loader2, MoreHorizontal, Trash2, Pencil,
  MapPin, Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const REACTION_EMOJIS = [
  "\u2764\ufe0f", "\ud83d\ude4f", "\ud83d\ude22",
  "\ud83d\ude02", "\ud83d\ude2e", "\ud83d\udc4f",
  "\ud83d\udd6f\ufe0f", "\ud83c\udf38",
];
const MEMORY_TAG_SUGGESTIONS = [
  "Childhood", "Wedding", "Migration", "Family Reunion", "Career",
  "Faith", "Travel", "Anniversary", "First meeting", "Story",
];


export default function MemberForum({ memberId, canWrite, meUid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/family-members/${memberId}/forum`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the message board");
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const onPostCreated = (post) => setItems((prev) => [{ ...post, replies: [] }, ...prev]);
  const onPostUpdated = (updated) =>
    setItems((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated, replies: p.replies || [] } : p)));
  const onReplyCreated = (parentId, reply) =>
    setItems((prev) => prev.map((p) => (p.id === parentId ? { ...p, replies: [...(p.replies || []), reply] } : p)));
  const onReplyUpdated = (parentId, updated) =>
    setItems((prev) => prev.map((p) => (p.id === parentId ? {
      ...p, replies: (p.replies || []).map((r) => (r.id === updated.id ? updated : r)),
    } : p)));
  const onPostDeleted = (postId) => setItems((prev) => prev.filter((p) => p.id !== postId));
  const onReplyDeleted = (parentId, replyId) =>
    setItems((prev) => prev.map((p) => (p.id === parentId ? {
      ...p, replies: (p.replies || []).filter((r) => r.id !== replyId),
    } : p)));

  return (
    <section
      className="rounded-2xl border p-4 sm:p-6"
      style={{ borderColor: "#E8DFCF", background: "#FFFDF8" }}
      data-testid="member-forum"
    >
      <header className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5" style={{ color: "#B25D38" }} />
        <h2 className="text-lg font-semibold" style={{ color: "#3D2A17" }}>Family message board</h2>
        <span className="text-xs text-neutral-500">
          {items.length} {items.length === 1 ? "post" : "posts"}
        </span>
      </header>

      {canWrite ? (
        <Composer memberId={memberId} onSaved={onPostCreated} />
      ) : (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 mb-4">
          Sign in to post a memory or reaction here.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-neutral-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading conversation…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-neutral-500 text-sm">
          No posts yet — be the first to share a memory.
        </div>
      ) : (
        <ul className="space-y-4 mt-4">
          {items.map((p) => (
            <TopPost
              key={p.id}
              post={p}
              memberId={memberId}
              canWrite={canWrite}
              meUid={meUid}
              onUpdated={onPostUpdated}
              onDeleted={onPostDeleted}
              onReplyCreated={onReplyCreated}
              onReplyUpdated={onReplyUpdated}
              onReplyDeleted={onReplyDeleted}
            />
          ))}
        </ul>
      )}
    </section>
  );
}


/** Shared card layout for both top-level posts and replies. */
function PostCard({ post, memberId, canWrite, meUid, isReply, onUpdated, onDelete }) {
  const [showPicker, setShowPicker] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const isMine = meUid && post.author_id === meUid;
  const created = post.created_at ? new Date(post.created_at) : null;
  const initial = (post.author_name || "?").trim().slice(0, 1).toUpperCase();

  const react = async (em) => {
    try {
      const { data } = await http.post(`/family-members/${memberId}/forum/${post.id}/react`, { emoji: em });
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't react");
    } finally {
      setShowPicker(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    setBusy(true);
    try {
      await http.delete(`/family-members/${memberId}/forum/${post.id}`);
      onDelete?.(post.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusy(false); setMenuOpen(false);
    }
  };

  if (editing) {
    return (
      <Composer
        memberId={memberId}
        isEdit
        postId={post.id}
        initial={post}
        onSaved={(updated) => { onUpdated?.(updated); setEditing(false); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const testId = isReply ? `forum-reply-${post.id}` : `forum-post-${post.id}`;
  const reactionCounts = post.reaction_counts || {};
  const myReactions = post.my_reactions || [];

  return (
    <div
      className="rounded-lg bg-white border p-3"
      style={{ borderColor: "#E8DFCF" }}
      data-testid={testId}
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F6C583] to-[#C2410C] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-[#3D2A17]">{post.author_name}</div>
            <div className="text-[11px] text-neutral-500 flex items-center gap-2">
              {created && <span title={created.toISOString()}>{created.toLocaleDateString()}</span>}
              {post.updated_at && post.updated_at !== post.created_at && <span className="italic">edited</span>}
              {isMine && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setMenuOpen((v) => !v)}
                    data-testid={`forum-post-menu-${post.id}`}
                    className="p-0.5 hover:bg-neutral-100 rounded"
                    aria-label="Post actions"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <div
                      className="absolute right-0 top-6 z-10 rounded-md border bg-white shadow-lg py-1 min-w-[120px]"
                      style={{ borderColor: "#E8DFCF" }}
                    >
                      <button
                        type="button"
                        onClick={() => { setEditing(true); setMenuOpen(false); }}
                        data-testid={`forum-post-edit-${post.id}`}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 flex items-center gap-1.5"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={del}
                        disabled={busy}
                        data-testid={`forum-post-delete-${post.id}`}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 text-red-700 flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-[#3D2A17] whitespace-pre-wrap mt-1">{post.body}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            {post.memory_tag && (
              <span className="px-2 py-0.5 rounded-full bg-[#FFF3D6] text-[#8B4513] border border-[#F0E6D2] flex items-center gap-1">
                <Tag className="w-3 h-3" /> {post.memory_tag}
              </span>
            )}
            {post.location && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {post.location}
              </span>
            )}
          </div>
          {Object.keys(reactionCounts).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(reactionCounts).map(([em, count]) => {
                const mine = myReactions.includes(em);
                return (
                  <button
                    key={em}
                    type="button"
                    onClick={() => canWrite && react(em)}
                    disabled={!canWrite}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${mine ? "bg-[#FFF3D6] border-[#F6C583]" : "bg-white border-neutral-200"}`}
                    data-testid={`forum-post-reaction-${post.id}`}
                  >
                    <span>{em}</span> <span className="text-neutral-600">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
          {canWrite && (
            <div className="mt-2 flex items-center gap-3 relative">
              <button
                type="button"
                onClick={() => setShowPicker((v) => !v)}
                data-testid={`forum-post-react-toggle-${post.id}`}
                className="text-[11px] text-neutral-600 hover:text-[#B25D38] flex items-center gap-1"
              >
                <Heart className="w-3 h-3" /> React
              </button>
              {showPicker && (
                <div
                  className="absolute z-10 mt-8 flex gap-1 px-2 py-1 rounded-full bg-white shadow-lg border"
                  style={{ borderColor: "#E8DFCF" }}
                >
                  {REACTION_EMOJIS.map((em, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => react(em)}
                      data-testid={`forum-post-reaction-pick-${post.id}-${i}`}
                      className="text-lg hover:scale-125 transition-transform"
                      aria-label="React"
                    >
                      {em}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/** Top-level post: shows the card, a Reply composer, and its replies. */
function TopPost({ post, memberId, canWrite, meUid, onUpdated, onDeleted, onReplyCreated, onReplyUpdated, onReplyDeleted }) {
  const [showReplyBox, setShowReplyBox] = useState(false);

  return (
    <li>
      <PostCard
        post={post}
        memberId={memberId}
        canWrite={canWrite}
        meUid={meUid}
        onUpdated={onUpdated}
        onDelete={onDeleted}
      />
      {canWrite && (
        <div className="mt-1 ml-10">
          <button
            type="button"
            onClick={() => setShowReplyBox((v) => !v)}
            data-testid={`forum-post-reply-toggle-${post.id}`}
            className="text-[11px] text-neutral-600 hover:text-[#B25D38] flex items-center gap-1"
          >
            <MessageCircle className="w-3 h-3" /> Reply
          </button>
        </div>
      )}
      {showReplyBox && (
        <div className="mt-2 ml-10">
          <Composer
            memberId={memberId}
            parentId={post.id}
            onSaved={(reply) => { onReplyCreated?.(post.id, reply); setShowReplyBox(false); }}
            onCancel={() => setShowReplyBox(false)}
          />
        </div>
      )}
      {(post.replies || []).length > 0 && (
        <ul className="mt-2 space-y-2 ml-8 pl-3 border-l-2" style={{ borderColor: "#F0E6D2" }}>
          {post.replies.map((r) => (
            <li key={r.id}>
              <PostCard
                post={r}
                memberId={memberId}
                canWrite={canWrite}
                meUid={meUid}
                isReply
                onUpdated={(updated) => onReplyUpdated?.(post.id, updated)}
                onDelete={(rid) => onReplyDeleted?.(post.id, rid)}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}


/**
 * Composer — handles top-level post OR reply OR edit, depending on
 * which props are supplied. Uses PATCH when ``isEdit`` is truthy,
 * otherwise POST.
 */
function Composer({ memberId, parentId, initial, isEdit, postId, onSaved, onCancel }) {
  const [body, setBody] = useState(initial?.body || "");
  const [memoryTag, setMemoryTag] = useState(initial?.memory_tag || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [showExtras, setShowExtras] = useState(!!(initial?.memory_tag || initial?.location));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (body.trim().length < 1) return;
    setBusy(true);
    try {
      let res;
      if (isEdit && postId) {
        res = await http.patch(`/family-members/${memberId}/forum/${postId}`, {
          body: body.trim(),
          memory_tag: memoryTag.trim() || null,
          location: location.trim() || null,
        });
      } else {
        res = await http.post(`/family-members/${memberId}/forum`, {
          body: body.trim(),
          parent_id: parentId || null,
          memory_tag: memoryTag.trim() || null,
          location: location.trim() || null,
        });
      }
      onSaved?.(res.data);
      if (!isEdit) {
        setBody(""); setMemoryTag(""); setLocation(""); setShowExtras(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post");
    } finally {
      setBusy(false);
    }
  };

  const composerTestId = parentId
    ? `forum-reply-body-${parentId}`
    : (isEdit ? `forum-edit-body-${postId}` : "forum-composer-body");
  const submitTestId = parentId
    ? `forum-reply-submit-${parentId}`
    : (isEdit ? `forum-edit-save-${postId}` : "forum-composer-submit");
  const scopeSuffix = parentId ? `-${parentId}` : (isEdit ? `-${postId}` : "");

  return (
    <div className="rounded-lg border bg-white p-3" style={{ borderColor: "#E8DFCF" }}>
      <textarea
        data-testid={composerTestId}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={parentId ? "Write a reply…" : "Share a memory, thought, or condolence…"}
        rows={parentId ? 2 : 3}
        maxLength={5000}
        className="w-full px-2 py-1.5 text-sm focus:outline-none resize-none"
      />
      {showExtras && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <div>
            <label className="text-[11px] font-medium text-neutral-600 flex items-center gap-1"><Tag className="w-3 h-3" /> Memory tag</label>
            <input
              type="text"
              data-testid={`forum-composer-memory-tag${scopeSuffix}`}
              list={`memory-tags-${memberId}`}
              value={memoryTag}
              onChange={(e) => setMemoryTag(e.target.value)}
              placeholder="Wedding, Childhood, Migration…"
              maxLength={60}
              className="w-full rounded border border-neutral-200 px-2 py-1 text-xs focus:border-[#B25D38] focus:outline-none"
            />
            <datalist id={`memory-tags-${memberId}`}>
              {MEMORY_TAG_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div>
            <label className="text-[11px] font-medium text-neutral-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</label>
            <input
              type="text"
              data-testid={`forum-composer-location${scopeSuffix}`}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Kottayam, Kerala"
              maxLength={120}
              className="w-full rounded border border-neutral-200 px-2 py-1 text-xs focus:border-[#B25D38] focus:outline-none"
            />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: "#F0E6D2" }}>
        <button
          type="button"
          onClick={() => setShowExtras((v) => !v)}
          data-testid={`forum-composer-extras-toggle${scopeSuffix}`}
          className="text-[11px] text-[#B25D38] hover:underline flex items-center gap-1"
        >
          <Tag className="w-3 h-3" />
          {showExtras ? "Hide tags" : "Add memory tag / location"}
        </button>
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
          )}
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || body.trim().length < 1}
            style={{ background: body.trim().length ? "#B25D38" : "#D6C7A4", color: "#FFF" }}
            data-testid={submitTestId}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4 mr-1" />
                {isEdit ? "Save" : (parentId ? "Reply" : "Post")}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
